/*
 * Sourced from https://v0.dev/chat/menu-navbar-sidebar-w-o-localization-Y0lZuopJCUv
 * See docs/v0.md for more
 */

'use client'

import { useEffect } from 'react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

import { ChevronDownIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSidebar } from '@/components/ui/sidebar'
import { useSession } from '@/app/hooks/use-session'
import { trackNavClickEvent } from '@/services/auth/client/auth-events'

export interface MenuItem {
    title: string
    shortTitle?: string
    url: string
    description?: string
    icon?: React.ReactNode
    items?: MenuItem[]
}

export interface NavbarLogo {
    url: string
    src: string
    alt: string
    width?: number
    height?: number
}

export interface NavbarAuth {
    login: {
        title: string
        url: string
    }
    signup: {
        title: string
        url: string
    }
    myTrips: {
        title: string
        url: string
    }
    account: {
        title: string
        url: string
    }
    points: {
        title: string
        url: string
    }
}

export interface NavbarProps {
    logo: NavbarLogo
    menu: MenuItem[]
    auth?: NavbarAuth
    /** Optional brand element rendered beside the logo (e.g. the blog masthead). */
    brandSlot?: React.ReactNode
    /** When set with `brandSlot`, wraps the slot in a link to this URL. */
    brandUrl?: string
}

const navLinkStyle =
    'inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1'

export const Navbar = ({ logo, menu, auth, brandSlot, brandUrl }: NavbarProps) => {
    const t = useTranslations('navigation')
    const { session, logout } = useSession()
    const isLoggedIn = session.isAuthenticated
    const firstName = session.firstName ?? ''
    const { toggleSidebar, setOpenMobile } = useSidebar()
    const isMobile = useIsMobile()

    // Close sidebar when switching from mobile to desktop
    useEffect(() => {
        if (!isMobile) {
            setOpenMobile(false)
        }
    }, [isMobile, setOpenMobile])

    return (
        <header className="sticky top-0 z-30 w-full border-b bg-background lg:static">
            <div className="mx-auto flex h-[50px] w-full items-center justify-between px-5 lg:h-[70px] lg:w-[980px] lg:px-0">
                {/* Left side: Logo and Navigation */}
                <div className="flex items-center gap-4 max-sm:min-w-0">
                    {/* Logo */}
                    <a href={logo.url} className="flex items-center rounded-md focus:outline-none">
                        <Image
                            priority
                            src={logo.src}
                            alt={logo.alt}
                            width={logo.width}
                            height={logo.height}
                            className="h-8 w-auto"
                        />
                    </a>

                    {/* Optional brand element beside the logo (blog masthead),
                        wrapped in a link when a brand URL is supplied. */}
                    {brandSlot &&
                        (brandUrl ? (
                            <a
                                href={brandUrl}
                                className="border-l border-gray-300 pl-5 max-sm:min-w-0"
                            >
                                {brandSlot}
                            </a>
                        ) : (
                            brandSlot
                        ))}

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex" aria-label={t('mainNavigation')}>
                        <ul className="flex list-none items-center gap-1">
                            {menu.map((item) =>
                                item.items ? (
                                    <li key={item.title} className="group/nav-item relative">
                                        <a href={item.url} className={cn(navLinkStyle, 'gap-1')}>
                                            {item.title}
                                            <ChevronDownIcon
                                                className="relative top-[1px] ml-1 size-3 transition duration-300 group-hover/nav-item:rotate-180"
                                                aria-hidden="true"
                                            />
                                        </a>
                                        <div className="invisible absolute top-full left-0 z-50 mt-1.5 w-72 rounded-md border bg-popover p-4 text-popover-foreground opacity-0 shadow transition-all duration-200 group-focus-within/nav-item:visible group-focus-within/nav-item:opacity-100 group-hover/nav-item:visible group-hover/nav-item:opacity-100 before:absolute before:-top-2 before:left-0 before:h-2 before:w-full before:content-['']">
                                            <div className="grid gap-1">
                                                {item.items.map((subItem) => (
                                                    <a
                                                        key={subItem.url}
                                                        href={subItem.url}
                                                        className="block rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                                                    >
                                                        {subItem.title}
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    </li>
                                ) : (
                                    <li key={item.title}>
                                        <a href={item.url} className={navLinkStyle}>
                                            {item.title}
                                        </a>
                                    </li>
                                ),
                            )}
                        </ul>
                    </nav>
                </div>

                {/* Right side: User Menu */}
                {auth && (
                    <div className="hidden items-center lg:flex">
                        {/* Uncontrolled: the primitive owns its open state, which is what
                            makes the menu explicit-activation only (FR-14) — click/tap and
                            Enter/Space/ArrowDown open it, Escape and an outside press close
                            it. Do not layer a hover-open listener over it: `mouseenter`
                            fires before the trigger's own `pointerdown`, so a hover listener
                            puts click out of phase and the press that follows toggles the
                            menu straight back shut (issue #494, RD-3). */}
                        <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="group flex min-w-[96px] items-center justify-center gap-1 text-sm font-medium text-foreground hover:bg-transparent"
                                >
                                    {/* SSR/SSG renders logged-out (session-blind, cache-safe);
                                        after hydration useSession resolves the mirror and the
                                        control re-renders. The min-width keeps the swap from
                                        shifting layout (CLS). */}
                                    {isLoggedIn ? (
                                        <span>
                                            {firstName
                                                ? t('greeting', { name: firstName })
                                                : t('myAccount')}
                                        </span>
                                    ) : (
                                        <span>{t('logIn')}</span>
                                    )}
                                    <ChevronDownIcon
                                        className="relative top-[1px] ml-1 size-3 transition duration-300 group-data-[state=open]:rotate-180"
                                        aria-hidden="true"
                                    />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-72">
                                <div className="p-6">
                                    {/* Logged-out CTAs. Account links below render in both
                                        states (ui-react AccountMenuContent parity). */}
                                    {!isLoggedIn && (
                                        <div className="space-y-2.5">
                                            <Button
                                                asChild
                                                variant="strong"
                                                size="lg"
                                                className="w-full font-bold tracking-[2px] uppercase"
                                            >
                                                <a href={auth.signup.url}>{auth.signup.title}</a>
                                            </Button>
                                            <Button
                                                asChild
                                                variant="strong-outline"
                                                size="lg"
                                                className="w-full font-bold tracking-[2px] uppercase"
                                            >
                                                <a
                                                    href={auth.login.url}
                                                    onClick={() =>
                                                        trackNavClickEvent({
                                                            label: 'log_in',
                                                            navType: 'nav_bar',
                                                        })
                                                    }
                                                >
                                                    {auth.login.title}
                                                </a>
                                            </Button>
                                        </div>
                                    )}
                                    <div className={cn('flex flex-col', !isLoggedIn && 'mt-6')}>
                                        <DropdownMenuItem asChild>
                                            <a
                                                href={auth.myTrips.url}
                                                className="cursor-pointer py-3 text-sm focus:bg-muted"
                                            >
                                                {auth.myTrips.title}
                                            </a>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <a
                                                href={auth.account.url}
                                                className="cursor-pointer py-3 text-sm focus:bg-muted"
                                            >
                                                {auth.account.title}
                                            </a>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <a
                                                href={auth.points.url}
                                                className="cursor-pointer py-3 text-sm focus:bg-muted"
                                            >
                                                {auth.points.title}
                                            </a>
                                        </DropdownMenuItem>
                                        {isLoggedIn && (
                                            <>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    className="cursor-pointer py-3 text-sm focus:bg-muted"
                                                    onSelect={() => {
                                                        trackNavClickEvent({
                                                            label: 'log_out',
                                                            navType: 'nav_bar',
                                                        })
                                                        void logout()
                                                    }}
                                                >
                                                    {t('logOut')}
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )}

                {/* Mobile Menu Button - Hamburger icon with larger size */}
                <div className="flex items-center lg:hidden">
                    <button
                        type="button"
                        className="flex h-5 w-[30px] flex-col justify-between"
                        onClick={toggleSidebar}
                        aria-label={t('toggleSidebar')}
                    >
                        <span className="h-[3px] w-full rounded-full bg-[#2e3033]" />
                        <span className="h-[3px] w-full rounded-full bg-[#2e3033]" />
                        <span className="h-[3px] w-full rounded-full bg-[#2e3033]" />
                    </button>
                </div>
            </div>
        </header>
    )
}
